// The game's sound, made on the spot with Web Audio: nothing to download. One context, opened by
// the Play button, one quiet master bus, and every voice enveloped so nothing clicks. Muted, or in
// a browser without Web Audio, every sound does nothing. And a buzz, on phones that have one.

import { createStore, useStore } from "../state/store";

// ---------- the context ----------

/** The master bus: everything together stays well under full scale. */
const MASTER = 0.35;
/** Sounds start this far ahead of now, so the first sample is never in the past. */
const LEAD = 0.005;
/** Silence, as near as an exponential ramp can get. */
const FLOOR = 0.0001;

let context: AudioContext | null = null;
let bus: GainNode | null = null;
let noise: AudioBuffer | null = null;
let crunchCurve: WaveShaperNode["curve"] = null;

type Out = { ac: AudioContext; bus: GainNode; t: number };

// ---------- muted ----------

const KEY = "newspaper.hl:muted:1";

function readMuted(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** Sound and buzz off, remembered in this browser. On by default. */
export const muted = createStore<boolean>(readMuted());

muted.subscribe(() => {
  const off = muted.get();
  try {
    localStorage.setItem(KEY, off ? "1" : "0");
  } catch {
    // storage blocked: kept for this tab
  }
  // whatever is still ringing fades rather than cuts
  if (context && bus) {
    try {
      bus.gain.setTargetAtTime(off ? 0 : MASTER, context.currentTime, 0.02);
    } catch {
      // closed
    }
  }
});

export const useMuted = (): boolean => useStore(muted);
export const isMuted = (): boolean => muted.get();
export function setMuted(next: boolean): void {
  muted.set(next);
}

/** Opens the one shared AudioContext, or wakes it. Call it from a tap; calling it again is free. */
export function unlock(): void {
  try {
    if (!context) {
      const scope = globalThis as typeof globalThis & { webkitAudioContext?: typeof AudioContext };
      const Context = scope.AudioContext ?? scope.webkitAudioContext;
      if (!Context) return;
      const ac = new Context();
      const gain = ac.createGain();
      gain.gain.value = muted.get() ? 0 : MASTER;
      // a soft limiter: a bust over a run of ticks never clips
      const limiter = ac.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.knee.value = 6;
      limiter.ratio.value = 12;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.12;
      gain.connect(limiter).connect(ac.destination);
      context = ac;
      bus = gain;
    }
    if (context.state !== "running") {
      void context.resume().catch(() => undefined);
      // older WebKit wakes only once something has played inside the gesture
      const blank = context.createBufferSource();
      blank.buffer = context.createBuffer(1, 1, context.sampleRate);
      blank.connect(context.destination);
      blank.start();
    }
  } catch {
    context = null;
    bus = null;
  }
}

/** Where a sound goes now, or null when it should stay quiet. */
function out(): Out | null {
  if (!context || !bus || muted.get()) return null;
  if (context.state !== "running") {
    // put to sleep by the browser (a call, a locked phone): ask again, quiet this time
    try {
      void context.resume().catch(() => undefined);
    } catch {
      // closed
    }
    return null;
  }
  return { ac: context, bus, t: context.currentTime + LEAD };
}

/** Plays when sound may; anything a partial Web Audio throws stays here. */
function play(make: (o: Out) => void): void {
  const o = out();
  if (!o) return;
  try {
    make(o);
  } catch {
    // this one stays silent
  }
}

// ---------- voices ----------

/** One second of white noise, made once and looped from a random point. */
function noiseOf(ac: AudioContext): AudioBuffer {
  if (noise && noise.sampleRate === ac.sampleRate) return noise;
  const buffer = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  noise = buffer;
  return buffer;
}

/** A soft clip, for the bust's crunch. */
function crunchOf(): WaveShaperNode["curve"] {
  if (crunchCurve) return crunchCurve;
  const size = 1024;
  const curve = new Float32Array(size);
  for (let i = 0; i < size; i++) curve[i] = Math.tanh(6 * ((i / (size - 1)) * 2 - 1)) / Math.tanh(6);
  crunchCurve = curve;
  return curve;
}

/** A gain that rises to `peak` in `attack` s, then dies away over `decay` s. */
function envelope(ac: AudioContext, start: number, peak: number, attack: number, decay: number): GainNode {
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(Math.max(FLOOR * 2, peak), start + attack);
  gain.gain.exponentialRampToValueAtTime(FLOOR, start + attack + decay);
  gain.gain.linearRampToValueAtTime(0, start + attack + decay + 0.01);
  return gain;
}

type Shape = {
  /** Seconds after now. */
  at?: number;
  hz: number;
  /** Glides here over `glide` s, the whole sound when unset. */
  to?: number;
  glide?: number;
  peak: number;
  attack: number;
  decay: number;
  /** Into this node instead of the bus. */
  into?: AudioNode;
};

/** One enveloped oscillator. */
function tone(o: Out, wave: OscillatorType, v: Shape): void {
  const start = o.t + (v.at ?? 0);
  const osc = o.ac.createOscillator();
  osc.type = wave;
  osc.frequency.setValueAtTime(v.hz, start);
  if (v.to !== undefined) osc.frequency.exponentialRampToValueAtTime(v.to, start + (v.glide ?? v.attack + v.decay));
  const gain = envelope(o.ac, start, v.peak, v.attack, v.decay);
  osc.connect(gain).connect(v.into ?? o.bus);
  osc.onended = () => {
    osc.disconnect();
    gain.disconnect();
  };
  osc.start(start);
  osc.stop(start + v.attack + v.decay + 0.02);
}

/** Enveloped noise through one filter. */
function hiss(o: Out, filter: BiquadFilterType, v: Shape & { q?: number }): void {
  const start = o.t + (v.at ?? 0);
  const source = o.ac.createBufferSource();
  source.buffer = noiseOf(o.ac);
  source.loop = true;
  const shape = o.ac.createBiquadFilter();
  shape.type = filter;
  shape.Q.value = v.q ?? 1;
  shape.frequency.setValueAtTime(v.hz, start);
  if (v.to !== undefined) shape.frequency.exponentialRampToValueAtTime(v.to, start + (v.glide ?? v.attack + v.decay));
  const gain = envelope(o.ac, start, v.peak, v.attack, v.decay);
  source.connect(shape).connect(gain).connect(v.into ?? o.bus);
  source.onended = () => {
    source.disconnect();
    shape.disconnect();
    gain.disconnect();
  };
  source.start(start, Math.random() * 0.8);
  source.stop(start + v.attack + v.decay + 0.02);
}

// ---------- the sounds ----------

export type Fx = {
  /** The headline lands: a press stamp, about 200 ms. */
  headline(): void;
  /** A soft tick at 3, 2 and 1 seconds left, higher at 1. Anything else is ignored. */
  countdown(secondsLeft: number): void;
  /** The position opens: a whoosh, rising for long, falling for short. */
  open(side: "long" | "short"): void;
  /** One step of the replay: `move` is the signed fraction of margin since the last call. */
  tick(move: number): void;
  /** Liquidated: a low crunch and a burst of noise, about 800 ms. */
  bust(): void;
  /** A win, `size` 0 to 1: a rising chime of 2 to 4 notes. */
  win(size: number): void;
  /** A loss: a short muffled thud. */
  lose(): void;
  /** Closing a position: a crisp switch click. */
  close(): void;
};

let lastHeadline = -1;
/** The countdown's last tick, so a caller polling the clock ticks once a second. */
let lastCount = { n: 0, at: -1 };

/** The replay's ticks: what has moved since the last click, and when. */
let pending = 0;
let lastCall = -1;
let lastClick = -1;
const TICKS_PER_S = 12;

export const fx: Fx = {
  headline() {
    play((o) => {
      // once per landing, however many times the page asks
      if (o.t - lastHeadline < 0.15) return;
      lastHeadline = o.t;
      // the platen: a low knock that drops
      tone(o, "sine", { hz: 150, to: 52, glide: 0.12, peak: 0.75, attack: 0.002, decay: 0.17 });
      // type meeting paper
      hiss(o, "bandpass", { hz: 1800, to: 700, glide: 0.06, q: 0.9, peak: 0.45, attack: 0.001, decay: 0.07 });
      // the strike, and the bar settling a beat after
      hiss(o, "highpass", { hz: 3500, q: 0.7, peak: 0.22, attack: 0.0005, decay: 0.018 });
      hiss(o, "bandpass", { at: 0.055, hz: 2600, q: 1.5, peak: 0.08, attack: 0.0005, decay: 0.02 });
    });
  },

  countdown(secondsLeft) {
    const n = Math.round(secondsLeft);
    if (n < 1 || n > 3) return;
    play((o) => {
      if (n === lastCount.n && o.t - lastCount.at < 0.8) return;
      lastCount = { n, at: o.t };
      const hz = n === 1 ? 1020 : 760;
      // a woodblock, far off
      tone(o, "sine", { hz, to: hz * 0.94, peak: 0.16, attack: 0.002, decay: 0.07 });
      tone(o, "triangle", { hz: hz * 2, peak: 0.025, attack: 0.001, decay: 0.025 });
      hiss(o, "bandpass", { hz: hz * 3, q: 3, peak: 0.05, attack: 0.0005, decay: 0.01 });
    });
  },

  open(side) {
    const up = side === "long";
    play((o) => {
      // air moving: noise swept through a band
      hiss(o, "bandpass", { hz: up ? 380 : 3200, to: up ? 3200 : 380, glide: 0.22, q: 1.6, peak: 0.42, attack: 0.05, decay: 0.19 });
      tone(o, "triangle", { hz: up ? 180 : 360, to: up ? 360 : 180, glide: 0.2, peak: 0.12, attack: 0.03, decay: 0.19 });
      // the fill locks in
      hiss(o, "bandpass", { at: 0.2, hz: 2400, q: 2, peak: 0.12, attack: 0.0005, decay: 0.015 });
    });
  },

  tick(move) {
    if (!Number.isFinite(move)) return;
    play((o) => {
      const now = o.t;
      if (now - lastCall > 0.4) pending = 0;
      lastCall = now;
      pending += move;
      if (now - lastClick < 1 / TICKS_PER_S) return;
      const size = Math.abs(pending);
      // flat: nothing to count
      if (size < 0.001) return;
      // a Geiger counter: the bigger the move, the likelier the click
      if (Math.random() > 0.3 + size * 8) return;
      lastClick = now;
      const sign = Math.sign(pending);
      pending = 0;
      // pitch follows the move: up is higher, a bigger move further from the middle
      const reach = sign * Math.sqrt(Math.min(1, size / 0.5));
      const hz = 1250 * 2 ** (1.25 * reach) * (0.985 + Math.random() * 0.03);
      // and leans the way it went
      tone(o, "sine", { hz: sign > 0 ? hz * 0.94 : hz, to: sign > 0 ? hz : hz * 0.94, peak: 0.05 + 0.07 * Math.abs(reach), attack: 0.001, decay: 0.028 });
      hiss(o, "bandpass", { hz: hz * 1.6, q: 5, peak: 0.05, attack: 0.0005, decay: 0.006 });
    });
  },

  bust() {
    play((o) => {
      // the impact: a sub that drops away
      tone(o, "sine", { hz: 95, to: 32, glide: 0.45, peak: 0.8, attack: 0.003, decay: 0.75 });
      // the crunch: torn low tones through a soft clip, a lowpass closing on them
      const shaper = o.ac.createWaveShaper();
      shaper.curve = crunchOf();
      shaper.oversample = "2x";
      const dull = o.ac.createBiquadFilter();
      dull.type = "lowpass";
      dull.Q.value = 0.8;
      dull.frequency.setValueAtTime(1400, o.t);
      dull.frequency.exponentialRampToValueAtTime(160, o.t + 0.5);
      shaper.connect(dull).connect(o.bus);
      tone(o, "sawtooth", { hz: 62, to: 38, glide: 0.5, peak: 0.45, attack: 0.002, decay: 0.55, into: shaper });
      tone(o, "square", { hz: 93, to: 45, glide: 0.4, peak: 0.2, attack: 0.002, decay: 0.4, into: shaper });
      // the burst: noise from bright to dull, then debris a beat later
      hiss(o, "lowpass", { hz: 5000, to: 260, glide: 0.4, q: 0.6, peak: 0.45, attack: 0.002, decay: 0.45 });
      hiss(o, "bandpass", { at: 0.07, hz: 1600, to: 500, glide: 0.25, q: 1.2, peak: 0.15, attack: 0.002, decay: 0.28 });
      globalThis.setTimeout(() => {
        shaper.disconnect();
        dull.disconnect();
      }, 1500);
    });
  },

  win(size) {
    const s = Number.isFinite(size) ? Math.min(1, Math.max(0, size)) : 0;
    const count = s < 0.34 ? 2 : s < 0.67 ? 3 : 4;
    // E5, G#5, B5, E6: a major arpeggio, climbing
    const notes = [659.26, 830.61, 987.77, 1318.51];
    play((o) => {
      for (let i = 0; i < count; i++) {
        const hz = notes[i] ?? 1318.51;
        const at = i * 0.075;
        const decay = i === count - 1 ? 0.7 + 0.3 * s : 0.35;
        // a clean sine, an octave of sheen and a faint bell partial for the strike
        tone(o, "sine", { at, hz, peak: 0.2, attack: 0.004, decay });
        tone(o, "sine", { at, hz: hz * 2, peak: 0.05, attack: 0.002, decay: decay * 0.5 });
        tone(o, "sine", { at, hz: hz * 2.76, peak: 0.025, attack: 0.001, decay: decay * 0.25 });
      }
    });
  },

  lose() {
    play((o) => {
      tone(o, "sine", { hz: 130, to: 62, glide: 0.18, peak: 0.55, attack: 0.004, decay: 0.24 });
      // a little body, for phone speakers that drop the low end
      tone(o, "triangle", { hz: 196, to: 150, glide: 0.15, peak: 0.08, attack: 0.004, decay: 0.12 });
      hiss(o, "lowpass", { hz: 600, q: 0.7, peak: 0.12, attack: 0.002, decay: 0.06 });
    });
  },

  close() {
    play((o) => {
      // a well-made switch: the click, then the lighter release
      hiss(o, "bandpass", { hz: 3800, q: 1.4, peak: 0.32, attack: 0.0005, decay: 0.012 });
      tone(o, "sine", { hz: 1900, to: 1300, glide: 0.03, peak: 0.12, attack: 0.001, decay: 0.035 });
      hiss(o, "bandpass", { at: 0.045, hz: 5200, q: 1.8, peak: 0.14, attack: 0.0005, decay: 0.008 });
    });
  },
};

/** A buzz, in ms (or on, off, on...), where the phone has one and sound is on. */
export function haptic(pattern: number | number[]): void {
  if (muted.get()) return;
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(pattern);
  } catch {
    // not allowed here
  }
}
